import React, { useMemo } from 'react';
import { StyledTitleText, StyledSubText, StyledForm, StyledInputWrapper } from './RegistryProtected.styles';
import { Flex, Box, ButtonV2, InputV2, FormControl, DialogV2, defaultTheme } from '@withjoy/joykit';
import { ReactComponent as LockIcon } from '@assets/icons/heart-lock.svg';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { ContactHost } from './ContactHost';
import { useRegistryProtectedController } from './RegistryProtected.controller';
import { Category } from '@graphql/generated-base';
import { useLoadGoogleFont } from '@shared/hooks/useLoadGoogleFont';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import chroma from 'chroma-js';

interface Props {
  eventId?: string;
  eventHandle: string;
}

export const RegistryProtected: React.FC<Props> = ({ eventId, eventHandle }) => {
  const { getRegistryProtectedTranslations } = useRegistryGuestTranslations();
  const { title, subtitle, inputPlaceholder, actionButton, signInButton, contactButton } = getRegistryProtectedTranslations();
  const { showContactHost, setShowContactHost, registryProtectedFormik, handleSubmit, handleSigninClick, joinEventLoading } = useRegistryProtectedController(eventId);
  const inputError = registryProtectedFormik.errors.password;

  const { eventDesign } = useEventDesignThemingProvider();
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  const accentColor = pageFontColor || defaultTheme.colors.black;
  const buttonStyles = useMemo(() => getButtonStylesWithAccentColor(accentColor), [accentColor]);
  const inputStyles = useMemo(
    () => ({
      borderColor: chroma(accentColor).alpha(0.35).css(),
      focusColor: accentColor,
    }),
    [accentColor],
  );

  const headingFont = eventDesign?.fonts?.find(font => font.category === Category.HEADING);
  const headingFontFamily = headingFont?.font?.fontFamily;

  const subHeadingFont = eventDesign?.fonts?.find(font => font.category === Category.SUB_HEADING);
  const subHeadingFontFamily = subHeadingFont?.font?.fontFamily;

  // Load custom fonts for the dialog
  useLoadGoogleFont([headingFontFamily, subHeadingFontFamily].filter((f): f is string => !!f));

  return showContactHost ? (
    <ContactHost
      setShowContactHost={setShowContactHost}
      eventId={eventId}
      eventHandle={eventHandle}
      headingFontFamily={headingFontFamily}
      subHeadingFontFamily={subHeadingFontFamily}
    />
  ) : (
    <DialogV2
      id="registryIsPrivate"
      isOpen={true}
      size="sm"
    >
      <DialogV2.Body
        paddingX={[6, 8]}
        paddingY={8}
        backgroundColor={pageColor}
        color={pageFontColor}
        borderRadius={[0, 12]}
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Box marginBottom={22}>
            <LockIcon />
          </Box>
          <StyledTitleText
            typographyVariant="hed4"
            marginBottom={5}
            fontFamily={headingFontFamily}
          >
            {title}
          </StyledTitleText>
          <StyledSubText
            typographyVariant="body4"
            marginBottom={8}
            fontFamily={subHeadingFontFamily}
          >
            {subtitle}
          </StyledSubText>
          <StyledForm onSubmit={handleSubmit}>
            <StyledInputWrapper
              $focusColor={inputStyles.focusColor}
              marginBottom={8}
            >
              <FormControl
                label="password"
                error={inputError}
                isInvalid={!!inputError}
                overrides={{
                  Label: {
                    props: {
                      visibility: 'hidden',
                    },
                  },
                }}
              >
                <InputV2
                  type="text"
                  placeholder={inputPlaceholder}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  {...registryProtectedFormik.getFieldProps('password')}
                />
              </FormControl>
            </StyledInputWrapper>
            <ButtonV2
              shape="rounded"
              fullWidth
              variant="outline"
              marginBottom={8}
              disabled={!registryProtectedFormik.isValid || !registryProtectedFormik.dirty || joinEventLoading}
              loading={joinEventLoading}
              type="submit"
              fontFamily={subHeadingFontFamily}
              {...buttonStyles}
            >
              {actionButton}
            </ButtonV2>
          </StyledForm>
          <Flex
            width="100%"
            justifyContent="center"
            gap={6}
          >
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              color="inherit"
              onClick={handleSigninClick}
              fontFamily={subHeadingFontFamily}
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="currentColor"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
            >
              {signInButton}
            </ButtonV2>
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              color="inherit"
              onClick={() => {
                setShowContactHost(true);
              }}
              fontFamily={subHeadingFontFamily}
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="currentColor"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
            >
              {contactButton}
            </ButtonV2>
          </Flex>
        </Flex>
      </DialogV2.Body>
    </DialogV2>
  );
};
